import { Accordion, AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import classnames from 'classnames';
import { fetchDataSet } from 'js/actions';
import DataSetDataBrowserContainer from 'js/components/data_sets/cards/DataSetDataBrowserContainer';
import DataSourceDetails from 'js/components/data_sources/add_ds_form/DataSourceDetails/DataSourceDetails';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import { ReactComponent as Expand } from 'js/components/elements/treeview/assets/expandMoreFilled.svg';
import { CONNECTION_CATEGORY_REST } from 'js/constants/connectionCategories';
import {
  getFeatureFlagValue,
  isSourceFormRuntimeSelectionEnabled,
} from 'new/constants/featureFlags';
import { RestApiStepList } from 'new/components/flows/configure/RestAPISource/redesign';
import { SchemaEditorViewModeType } from 'js/constants/schemaEditor';
import { SUPPORT_KEY_SOURCE_FORM_REST } from 'js/constants/supportPageKeys';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import {
  filter,
  first,
  forOwn,
  has,
  isArray,
  isEmpty,
  isEqual,
  isNil,
  isPlainObject,
  isString,
  keys,
  keysIn,
  map,
  mapValues,
  replace,
  toInteger,
} from 'lodash';
import { ReactComponent as AddIcon } from 'new/components/icons/fi-rr-plus.svg';
import { ReactComponent as DeleteIcon } from 'new/components/icons/fi-rr-trash.svg';
import { NavScroller } from 'new/elements/NavScroller';
import { NavScrollerContext } from 'new/elements/NavScroller/NavScroller';
import { ResourcePopoverMenu } from 'new/elements/ResourcePopoverMenu';
import { UiMode } from 'new/constants/uiMode';
import { Button } from 'new/elements/support/Button';
import { tryCatchAsync } from 'new/utils/TryCatch';
import React from 'react';
import { connect } from 'react-redux';
import Constants from '../../../Constants';
import JSONUtils from '../../../lib/JSONUtils';
import AdvancedConfiguration from './AdvancedConfiguration';
import ConfigureAPIStage from './ConfigureAPIStage';
import MonitoringInterval from './MonitoringInterval/MonitoringInterval';
import ResizableHorizontalLayout from 'js/components/elements/ResizableHorizontalLayout';
import RuntimeSelection from 'new/modules/settings/capsules/components/RuntimeSelection/RuntimeSelection';
import RuntimeSelectionDescription from 'new/modules/settings/capsules/components/RuntimeSelection/RuntimeSelectionDescription';

import './ConfigureAPI.scss';

const getAPIURLMacros = (props) => {
  const {
    apiConfig: { dataMapKeys },
    apiResponseByStage,
    dataPathValidationResultByStage,
    dataMaps,
  } = props;

  let dataMapMacros = mapValues(dataMapKeys, (m, k) => {
    let mapId = replace(k, 'lookup', '');
    mapId = toInteger(mapId);
    let dataMap = has(dataMaps, mapId) ? dataMaps[mapId] : null;
    let mapName = dataMap ? dataMap.name : `Lookup: ${mapId}`;
    // Fall back to the data map's live schema columns when dataMapKeys has none yet
    // (its schema loaded after dataMapKeys was built), so 2nd/3rd lookups still yield
    // macros instead of showing an empty list.
    let columns =
      m && m.length
        ? m
        : keys((dataMap && dataMap.map_entry_schema && dataMap.map_entry_schema.properties) || {});
    // An empty lookup (no columns) still surfaces in the Lookup list — as a
    // non-selectable "(empty)" entry — so the user can see it's selected but has no
    // fields. Marked with `empty` so it's excluded from URL autocomplete.
    if (!columns.length) {
      return [
        { name: `__empty_${k}`, display_name: '', type: k, type_display: mapName, empty: true },
      ];
    }
    return map(columns, (item) => ({
      name: `{${k}.${item}}`,
      display_name: item,
      type: k,
      type_display: mapName,
    }));
  });

  //extract macros from api response
  let responseMacros = {};
  const restIterations = has(props.apiConfig, 'rest.iterations')
    ? props.apiConfig['rest.iterations']
    : null;
  if (!isNil(restIterations) && !isNil(apiResponseByStage)) {
    forOwn(apiResponseByStage, (stageRes, stageIdx) => {
      let responseToUse = {};
      //check if content type is 'application/json'.
      //TODO: extract macros for application/xml also
      let stageIdxNum = toInteger(stageIdx);
      if (
        restIterations.length > stageIdxNum &&
        has(restIterations[stageIdxNum], 'contentType') &&
        restIterations[stageIdxNum].contentType !== 'application/json'
      ) {
        //only use stages where response responses can be used for macros
        return;
      }

      let useDataPath = false;
      if (
        has(dataPathValidationResultByStage, stageIdx) &&
        has(dataPathValidationResultByStage[stageIdx], 'response.data.path')
      ) {
        const stageDataPath = dataPathValidationResultByStage[stageIdx]['response.data.path'];

        useDataPath = true;
        responseToUse =
          stageDataPath.status === true && has(stageDataPath, 'matches')
            ? stageDataPath.matches
            : responseToUse;
      }

      if (useDataPath === false && has(stageRes, 'apiResponseContent')) {
        responseToUse = stageRes['apiResponseContent'];
      }
      let responseObject = isString(responseToUse)
        ? JSONUtils.parseJson(responseToUse)
        : responseToUse;
      if (isArray(responseObject) && !isEmpty(responseObject)) {
        responseObject = first(responseObject);
      }
      let macroItems = null;
      if (isPlainObject(responseObject)) {
        macroItems = keysIn(responseObject);
      }

      if (!isEmpty(macroItems)) {
        let stageIdxKey = `${Constants.IterationPrefixes.STAGE}${stageIdxNum + 1}`;
        let stageIdxDisplayContent = `Step ${stageIdxNum + 1} Response`;
        responseMacros[stageIdxKey] = map(macroItems, (item) => ({
          name: `{${stageIdxKey}.${item}}`,
          display_name: item,
          type: stageIdxKey,
          type_display: stageIdxDisplayContent,
          stageId: stageIdxNum,
        }));
      }
    });
  }

  return { ...dataMapMacros, ...responseMacros };
};

const trash = <DeleteIcon width={16} height={16} className="delete-icon" />;

class ConfigureAPI extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activePanelKey: 0,
      apiURLMacros: getAPIURLMacros(props),
      macrosUpdateTime: null,
      runtimeSectionVisible: true,
    };

    this._handleAdvancedSettingChanged = this._handleAdvancedSettingChanged.bind(this);
    this._handlePanelSelected = this._handlePanelSelected.bind(this);
    this._handleStepRemoveClicked = this._handleStepRemoveClicked.bind(this);
    this._handleSamplesChanged = this._handleSamplesChanged.bind(this);
  }

  _handleRuntimeVisibilityChange = (visible) => {
    this.setState({ runtimeSectionVisible: visible });
  };

  _handleSamplesChanged(editedSamples) {
    this.setState({ editedSamples });
    if (this.props.onEditedSamplesChanged) {
      this.props.onEditedSamplesChanged(editedSamples);
    }
  }

  componentDidMount() {
    const { dataMaps, parentDataSet, isTemplatizedAPI } = this.props;

    if (isEmpty(dataMaps)) {
      this.props.loadDataMaps(false, ACLQueryModeTypes.MODE_COLLABORATOR.key);
    }

    if (parentDataSet && !isTemplatizedAPI) {
      this.props.fetchDataSet(parentDataSet.id);
    }

    this.props.onClearAPIResponse();

    this.props.setContextualSupport(SUPPORT_KEY_SOURCE_FORM_REST);
  }

  componentWillUnmount() {
    this.props.unsetContextualSupport(SUPPORT_KEY_SOURCE_FORM_REST);
  }

  componentDidUpdate(prevProps) {
    const { apiConfig, apiResponseUpdateTime } = prevProps;

    let { apiURLMacros, activePanelKey, macrosUpdateTime } = this.state;
    let apiMacrosUpdated = false,
      activeKeyUpdated = false;

    //get macro map for url templates
    if (apiResponseUpdateTime !== this.props.apiResponseUpdateTime) {
      //if the response structure was modified (check by timestamps)
      apiURLMacros = getAPIURLMacros(this.props);
      apiMacrosUpdated = true;
    } else {
      // If the data maps structure changed, refresh the macros. Compare the FULL
      // dataMapKeys (not just the key set) so macros also refresh when a lookup's
      // COLUMNS load after its key was already added — otherwise 2nd/3rd lookups whose
      // schema arrives late never appear in the macro list.
      if (!isEqual(apiConfig['dataMapKeys'], this.props.apiConfig['dataMapKeys'])) {
        apiURLMacros = getAPIURLMacros(this.props);
        apiMacrosUpdated = true;
      }
    }

    // if a stage has been added, expand its panel
    if (
      apiConfig['rest.iterations'] &&
      this.props.apiConfig['rest.iterations'] &&
      apiConfig['rest.iterations'] !== this.props.apiConfig['rest.iterations'] &&
      this.props.apiConfig['rest.iterations'].length !== apiConfig['rest.iterations'].length
    ) {
      const nextNonDataMapIterations = this._getNonDataMapIterations(
        this.props.apiConfig['rest.iterations'],
      );
      const nonDataMapIterations = this._getNonDataMapIterations(apiConfig['rest.iterations']);

      if (
        nextNonDataMapIterations.length > nonDataMapIterations.length ||
        nextNonDataMapIterations.length === 1
      ) {
        activePanelKey = nextNonDataMapIterations.length - 1;
        activeKeyUpdated = true;
      }
    }

    if (apiMacrosUpdated === true || activeKeyUpdated === true) {
      macrosUpdateTime = apiMacrosUpdated ? Date.now() : macrosUpdateTime;
      this.setState({
        activePanelKey,
        apiURLMacros,
        macrosUpdateTime,
      });
    }
  }

  getResourceConfig = () => {
    const { sourceDescription, sourceName } = this.props;
    const clonedConfig = structuredClone(this.props.apiConfig);

    const name = this.props.name || sourceName || clonedConfig.name || '';
    const description =
      this.props.description || sourceDescription || clonedConfig.description || '';

    delete clonedConfig?.name;
    delete clonedConfig?.description;

    return {
      name,
      description,
      source_config: clonedConfig,
    };
  };

  onSaveAdvancedMode = async (configString) => {
    const { nextFormStage, onSaveAdvancedMode, onError = () => {} } = this.props;
    const { name, description, source_config, api_config } = JSON.parse(configString);

    if (!JSONUtils.isJSON(configString)) {
      onError('The provided JSON is invalid. Please check the syntax and try again.');
      return;
    }

    const config = source_config ?? api_config ?? {};
    const trimmedName = typeof name === 'string' ? name.trim() : name;

    const data = {
      ...config,
      ...(trimmedName ? { name } : {}),
      description,
      'ui.mode': UiMode.CODE,
    };

    const { error } = await tryCatchAsync(onSaveAdvancedMode(data));
    if (error) {
      onError(error.message);
      return;
    }

    nextFormStage();
  };

  onSwitchUiMode = () => {
    const { apiConfig, onSourceConfigChanged, onAPIConfigChanged } = this.props;
    if (typeof onSourceConfigChanged !== 'function') return;

    onSourceConfigChanged({ ...apiConfig, 'ui.mode': UiMode.VIEW });
  };

  render() {
    const {
      showAdvancedSettings,
      paging,
      dataMaps,
      dataMapSamples,
      selectedConnection,
      apiConfig,
      apiResponseByStage,
      isLoadingProbeSample,
      isLoadingDataMaps,
      dataPathValidationResultByStage,
      validateDataPath,
      setFormError,
      loadDataMaps,
      onCallAPI,
      onAPIConfigChanged,
      onGetParameterizedURLs,
      onAddStep,
      advancedFields,
      children,
      name,
      description,
      sourceName,
      sourceDescription,
      onInputChange,
      visibleFieldData,
      parentDataSet,
      parentDataSetSamples,
      isTemplatizedAPI,
    } = this.props;

    const { activePanelKey, apiURLMacros, macrosUpdateTime } = this.state;

    const nonDataMapIterations = this._getNonDataMapIterations(apiConfig['rest.iterations']);

    // Redesigned REST API source UI — rollout controlled by the GrowthBook flag
    // (org/env targeting lives there), so new + legacy screens run side-by-side
    // during rollout.
    const redesignEnabled = getFeatureFlagValue('UI_ENABLE_REST_API_SOURCE_REDESIGN');

    // Per-step nav (Step 1 → Step 2 → …), shown as a vertical chain in the rail;
    // each entry scrolls to its step. The redesign uses the redesigned step
    // anchors (rest-api-step-N) and omits the Add Step nav button (the redesigned
    // steps own their own Add Step).
    const navOptions = nonDataMapIterations.map(({ key = '' }, idx) => {
      return { id: redesignEnabled ? `rest-api-step-${idx}` : key, label: `Step ${idx + 1}` };
    });

    if (redesignEnabled) {
      // "API Steps & Chaining" header pill above the step chain (clicking it
      // scrolls to the first step).
      navOptions.unshift({
        element: (
          <NavScrollerContext.Consumer>
            {({ onScrollTo, activeScroll }) => (
              <button
                type="button"
                className={classnames('rail-steps-header', {
                  'rail-steps-header-active': activeScroll !== 'scheduling-section',
                })}
                data-testid="rail-steps-header"
                onClick={() => onScrollTo && onScrollTo('rest-api-step-0')()}
              >
                API Requests
              </button>
            )}
          </NavScrollerContext.Consumer>
        ),
      });
    }

    if (!redesignEnabled) {
      navOptions.push({
        element: (
          <NavScrollerContext.Consumer>
            {({ onScrollTo }) => (
              <Button
                color="primary"
                variant="contained"
                className="add-step"
                data-testid="add-another-step"
                onClick={() => {
                  onAddStep(() => {
                    onScrollTo?.(`step${nonDataMapIterations.length + 1}`)();
                  });
                }}
              >
                <span className="add-step-icon">
                  <AddIcon />
                </span>
                Add Step
              </Button>
            )}
          </NavScrollerContext.Consumer>
        ),
      });
    }

    const isAPIServerTarget = !!parentDataSet;

    const effectiveConnectionCategory = this.props.connectionType || CONNECTION_CATEGORY_REST;

    const showRuntimeSelectionUI = isSourceFormRuntimeSelectionEnabled(
      getFeatureFlagValue('UI_ENABLE_CAPSULES_SETTINGS'),
      getFeatureFlagValue('UI_ENABLE_RUNTIME_SELECTION'),
      effectiveConnectionCategory,
      this.props.flowType,
    );

    if (!isAPIServerTarget) {
      if (showRuntimeSelectionUI && this.state.runtimeSectionVisible) {
        navOptions.push({
          label: 'Select Runtime',
          id: 'select-runtime-section',
        });
      }
      navOptions.push({
        label: 'Schedule',
        id: 'scheduling-section',
      });
    }

    const cronField = advancedFields?.find((f) => f.name === 'start.cron');
    const fieldsWithoutScheduling =
      advancedFields?.filter(
        (field) => field.name !== 'start.cron' && field.type !== 'runtime_selection',
      ) || [];
    const resourceConfig = this.getResourceConfig();

    const _name = name || sourceName;
    const _description = description || sourceDescription;

    const configureAPIContent = (
      <NavScroller
        navOptions={navOptions}
        navClassName={redesignEnabled ? 'rest-redesign-nav' : ''}
        isColumnLayout={isAPIServerTarget}
        style={isAPIServerTarget || redesignEnabled ? {} : { paddingLeft: 16 }}
      >
        <NavScrollerContext.Consumer>
          {(consumer = {}) => {
            const runtimeNode =
              !isAPIServerTarget && showRuntimeSelectionUI && this.state.runtimeSectionVisible ? (
                <div
                  id="select-runtime-section"
                  aria-label="section-container"
                  className={classnames({
                    runtime_selection_section_card: true,
                    runtime_selection_section_card_focused:
                      consumer?.activeScroll === 'select-runtime-section',
                  })}
                >
                  <h2 className="runtime_selection_section_heading">
                    {this.props.runtimeSelectionDisabled ? 'Selected Runtime' : 'Select Runtime'}
                  </h2>
                  <RuntimeSelectionDescription readOnly={this.props.runtimeSelectionDisabled} />
                  <div className="runtime_selection_section_body">
                    <RuntimeSelection
                      flowType={this.props.flowType}
                      connectionType={effectiveConnectionCategory}
                      onSelectRuntime={this.props.onSelectRuntime}
                      disabled={this.props.runtimeSelectionDisabled}
                      selectedClusterId={this.props.selectedClusterId}
                      onVisibilityChange={this._handleRuntimeVisibilityChange}
                    />
                  </div>
                </div>
              ) : null;

            const scheduleNode = !isAPIServerTarget ? (
              <div
                id="scheduling-section"
                className={classnames({
                  scheduling_section: true,
                  scheduling_section_focused: consumer?.activeScroll === 'scheduling-section',
                })}
              >
                <MonitoringInterval
                  field={cronField}
                  config={visibleFieldData}
                  source={this.props.sourceBeingEdited}
                  showExternalTrigger={this.props.showExternalTrigger}
                  onConfigChanged={(data, fieldName, value) => {
                    this.props.onSourceConfigChanged(data, fieldName, value);
                    this._handleAdvancedSettingChanged(data, fieldName, value);
                  }}
                />
              </div>
            ) : null;

            return (
              <div
                id="configure-api-stages"
                className="accordion-group"
                style={{ marginBottom: 0 }}
              >
                {redesignEnabled ? (
                  <RestApiStepList
                    apiConfig={apiConfig}
                    apiURLMacros={apiURLMacros}
                    macrosUpdateTime={macrosUpdateTime}
                    apiResponseByStage={apiResponseByStage}
                    parentDataSet={parentDataSet}
                    parentDataSetSamples={parentDataSetSamples}
                    editedSamples={this.props.editedSamples}
                    credentialData={this.props.credentialData}
                    isLoadingProbeSample={isLoadingProbeSample}
                    isLoadingDataMaps={isLoadingDataMaps}
                    dataPathValidationResultByStage={dataPathValidationResultByStage}
                    selectedConnection={selectedConnection}
                    paging={paging}
                    dataMapSamples={dataMapSamples}
                    dataMaps={dataMaps}
                    isAPIServerTarget={isAPIServerTarget}
                    validateDataPath={validateDataPath}
                    setFormError={setFormError}
                    loadDataMaps={loadDataMaps}
                    onCallAPI={onCallAPI}
                    onAPIConfigChanged={onAPIConfigChanged}
                    onGetParameterizedURLs={onGetParameterizedURLs}
                    onAddStep={onAddStep}
                    onRemoveStep={this.props.onRemoveStep}
                    belowSteps={
                      <>
                        {runtimeNode}
                        {scheduleNode}
                      </>
                    }
                  />
                ) : (
                  map(nonDataMapIterations, (iteration, index) => {
                    const expanded = activePanelKey === index;
                    const focused = consumer?.activeScroll === iteration?.key;
                    const iterationInfo = iteration?.['url.template'];
                    const displayIterationInfo =
                      iterationInfo && iterationInfo.length > 128
                        ? `${iterationInfo.substring(0, 128)}...`
                        : iterationInfo;
                    return (
                      <Accordion
                        square
                        key={`${index}/${nonDataMapIterations.length}`}
                        id={iteration?.key}
                        data-testid={iteration?.key}
                        className="accordion-item"
                        expanded={expanded}
                        onChange={() => {
                          this._handlePanelSelected(index);
                          consumer.onScrollTo(iteration?.key)();
                        }}
                      >
                        <AccordionSummary
                          className={classnames({
                            'accordion-title': true,
                            'accordion-title-focused': focused && !expanded,
                            'accordion-title-focused-expanded': focused && expanded,
                          })}
                        >
                          <div className="accordion-title-content">
                            <h2>
                              <Expand
                                className={classnames('expand', {
                                  ['expand-open']: expanded,
                                  ['expand-close']: !expanded,
                                })}
                              />

                              <span data-testid="iteration-step">Step {index + 1}</span>
                            </h2>

                            {iterationInfo && (
                              <span
                                title={displayIterationInfo}
                                data-testid="iteration-url-template-info"
                                className="accordion-content-grow iteration-info"
                              >
                                {displayIterationInfo}
                              </span>
                            )}

                            {nonDataMapIterations.length > 1 && (
                              <Button
                                color="error"
                                data-testid={`remove-step-${index}`}
                                data-stage-index={index}
                                className="remove-step-button"
                                startIcon={trash}
                                onClick={(e) => {
                                  this._handleStepRemoveClicked(e, index, () => {
                                    consumer.onScrollTo(`step${index}`)();
                                  });
                                }}
                              >
                                Remove Step
                              </Button>
                            )}
                          </div>
                        </AccordionSummary>

                        <AccordionDetails className="accordion-body">
                          <div className="accordion-body-content">
                            <ConfigureAPIStage
                              isFocused={consumer?.activeScroll === iteration?.key}
                              stageIndex={index}
                              apiConfig={apiConfig}
                              apiURLMacros={apiURLMacros}
                              macrosUpdateTime={macrosUpdateTime}
                              activePanelKey={this.state.activePanelKey}
                              stageAPIConfig={apiConfig['rest.iterations'][index]}
                              apiResponse={apiResponseByStage[index]}
                              parentDataSet={parentDataSet}
                              parentDataSetSamples={parentDataSetSamples}
                              editedSamples={this.props.editedSamples}
                              isLoadingProbeSample={isLoadingProbeSample}
                              isLoadingDataMaps={isLoadingDataMaps}
                              dataPathValidationResult={dataPathValidationResultByStage[index]}
                              // dataMapSamples are only passed to trigger re-render of
                              // ParameterizedURLPreview when new samples are loaded
                              selectedConnection={selectedConnection}
                              paging={paging}
                              dataMapSamples={dataMapSamples}
                              dataMaps={dataMaps}
                              validateDataPath={validateDataPath}
                              setFormError={setFormError}
                              onLoadDataMaps={loadDataMaps}
                              onCallAPI={onCallAPI}
                              onAPIConfigChanged={onAPIConfigChanged}
                              onGetParameterizedURLs={onGetParameterizedURLs}
                              isAPIServerTarget={isAPIServerTarget}
                            />
                          </div>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                )}

                {/* In the redesign these render inside the left steps column
                    (passed via belowSteps) so they sit beside the response
                    preview; legacy keeps them full-width below. */}
                {!redesignEnabled && runtimeNode}
                {!redesignEnabled && scheduleNode}
              </div>
            );
          }}
        </NavScrollerContext.Consumer>
      </NavScroller>
    );

    return (
      <div className="advanced-configuration-container advanced-configuration-api-container">
        <div
          id="configure-api"
          className={classnames(
            'configure-api configure-api-source advanced-configuration-left-content',
            {
              'api-server-target': isAPIServerTarget,
            },
          )}
        >
          {!isTemplatizedAPI && (
            <DataSourceDetails
              name={_name}
              description={_description}
              onInputChange={onInputChange}
              leftView={
                <HeaderMetaConfiguration
                  enableInputFields={false}
                  connection={selectedConnection}
                  isDestination={false}
                  sourceOrDestinationChipChildren={
                    isAPIServerTarget ? null : (
                      <ResourcePopoverMenu
                        resourceConfig={resourceConfig}
                        connectorType={selectedConnection}
                        jsonConfigOnSave={this.onSaveAdvancedMode}
                        onSwitchUiMode={this.onSwitchUiMode}
                        resourcePersisted={!!this.props.sourceBeingEdited?.id}
                        jsonConfigOnClose={() => {
                          if (this.props.goBack) {
                            this.props.goBack();
                          }
                        }}
                      />
                    )
                  }
                />
              }
            />
          )}

          {children}

          {this.props.removeHelperSection ? null : (
            <div className="helper-section">
              <Typography variant="subtitle2" className="helper-section-title">
                Configure API Source
              </Typography>
              <Typography variant="subtitle2" className="helper-section-description">
                Fetch data from any valid API endpoint. You can also configure multiple steps to
                chain API endpoints.
              </Typography>
            </div>
          )}

          {isAPIServerTarget && !isTemplatizedAPI ? (
            <div className="api-server-target-horizontal-layout">
              <ResizableHorizontalLayout initialSizes={['25%', '75%']} minSizes={[360, 400]}>
                <div className="parent-data-set-container">
                  <DataSetDataBrowserContainer
                    data={parentDataSet}
                    defaultViewMode={SchemaEditorViewModeType.CODE}
                    compactHeader
                    darkEditor
                    allowEditing
                    onSamplesChanged={this._handleSamplesChanged}
                  />
                </div>
                <div className="configure-api-content-wrapper">{configureAPIContent}</div>
              </ResizableHorizontalLayout>
            </div>
          ) : (
            configureAPIContent
          )}

          {showAdvancedSettings && (
            <AdvancedConfiguration
              selectedConnection={selectedConnection}
              sourceConfigFields={fieldsWithoutScheduling}
              sourceConfig={apiConfig}
              onSourceConfigChanged={this._handleAdvancedSettingChanged}
              resourceMode="source"
              connectionType={selectedConnection}
              connectionCategory={CONNECTION_CATEGORY_REST}
            />
          )}
        </div>
      </div>
    );
  }

  _handleAdvancedSettingChanged(data, name, value) {
    this.props.onAPIConfigChanged(name, value, null, data);
  }

  _handlePanelSelected(panelKey) {
    const nextKey = panelKey !== this.state.activePanelKey ? panelKey : null;

    this.setState({ activePanelKey: nextKey });
  }

  _handleStepRemoveClicked(e, stageIndex, callback) {
    e.preventDefault();
    e.stopPropagation();

    this.setState(
      (prevState) => ({
        activePanelKey: stageIndex - 1,
      }),
      () => {
        this.props.onRemoveStep(stageIndex);

        callback && callback();
      },
    );
  }

  _getNonDataMapIterations(iterations) {
    return filter(iterations, (iteration) => iteration['iteration.type'] !== 'data.map.key.queue');
  }
}

ConfigureAPI.defaultProps = {
  showAdvancedSettings: true,
  showExternalTrigger: false,
};

export default connect(null, {
  fetchDataSet,
})(ConfigureAPI);
